import React, { useState, useEffect } from "react";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import Notification from "../components/Notification";
import InputField from "../components/InputField";
import AuthenticatedLayout from "../layouts/AuthenticatedLayout";
import { useNavigate } from "react-router-dom";
import { createSeller, deleteSeller, getSellers, updateSeller } from "../services/sellerService";
import Loading from "../components/Loading";
import { useAuth } from "../hooks/AuthProvider";

const SellerPage = () => {
	const [sellers, setSellers] = useState([]);
	const [filteredSellers, setFilteredSellers] = useState([]);
	const [newSellerName, setNewSellerName] = useState("");
	const [newSellerShortName, setNewSellerShortName] = useState("");
	const [editingSeller, setEditingSeller] = useState(null);
	const [editingSellerName, setEditingSellerName] = useState("");
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(null);
	const [errorEditingSeller, setErrorEditingSeller] = useState(null);
	const [searchTerm, setSearchTerm] = useState("");
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const auth = useAuth();

	useEffect(() => {
		setLoading(true);
		getSellers(auth)
			.then((data) => {
				setSellers(data);
				setFilteredSellers(data);
			})
			.catch(setError)
			.finally(() => setLoading(false));
	}, [auth]);

	const handleClickRow = (sellerId) => {
		navigate(`/show?seller=${sellerId}`);
	};

	// Gestisce l'aggiunta di un nuovo venditore
	const handleAddSeller = (e) => {
		e.preventDefault();
		setError(null);
		setSuccess(null);
		if (!newSellerName || !newSellerShortName) {
			setError("Nome e Short Name sono obbligatori");
			return;
		}
		createSeller(auth, newSellerShortName, newSellerName)
			.then((res) => {
				const newSeller = {
					id: res.id,
					name: newSellerName,
					short_name: newSellerShortName,
					invoiceCount: 0,
				};
				setSellers([newSeller, ...sellers]);
				setFilteredSellers([newSeller, ...sellers]); // Aggiorna anche i venditori filtrati
				setNewSellerName("");
				setNewSellerShortName("");
				setSuccess("Venditore aggiunto con successo");
			})
			.catch(setError);
	};

	// Gestisce la modifica di un venditore
	const handleEditSeller = (seller) => {
		setEditingSeller(seller);
		setEditingSellerName(seller.name);
	};

	// Salva le modifiche di un venditore
	const handleSaveEdit = () => {
		setErrorEditingSeller(null);
		setError(null);
		setSuccess(null);
		if (!editingSellerName) {
			setErrorEditingSeller("Nome e Short Name sono obbligatori");
			return;
		}

		updateSeller(auth, editingSeller.id, editingSellerName)
			.then((res) => {
				const updatedSellers = sellers.map((seller) =>
					seller.id === editingSeller.id ? { ...seller, name: editingSellerName } : seller
				);
				setSellers(updatedSellers);
				setFilteredSellers(updatedSellers); // Aggiorna anche i venditori filtrati
				setEditingSeller(null);
				setEditingSellerName("");
				setSuccess("Venditore aggiornato con successo");
			})
			.catch(setError);
	};

	// Gestisce l'eliminazione di un venditore
	const handleDeleteSeller = (sellerId) => {
		setError(null);
		setSuccess(null);
		deleteSeller(auth, sellerId)
			.then(() => {
				setSellers(sellers.filter((seller) => seller.id !== sellerId));
				setFilteredSellers(filteredSellers.filter((seller) => seller.id !== sellerId));
				setSuccess("Venditore eliminato con successo");
			})
			.catch(setError);
	};

	// Gestisce la ricerca dei venditori
	const handleSearchChange = (e) => {
		const searchValue = e.target.value;
		setSearchTerm(searchValue);

		// Filtro dei venditori in base al nome
		const filtered = sellers.filter((seller) =>
			seller.name.toLowerCase().includes(searchValue.toLowerCase())
		);
		setFilteredSellers(filtered);
	};

	if (loading) return <Loading />;

	return (
		<AuthenticatedLayout title="Gestione Venditori">
			{success && <Notification notification={{ type: 3, message: success }} />}
			{error && <Notification notification={{ type: 0, message: error }} />}

			{/* Form per aggiungere un nuovo venditore */}
			<form className="mb-5" onSubmit={handleAddSeller}>
				<h2 className="subtitle">Aggiungi un Nuovo Venditore</h2>
				<div className="columns">
					<div className="column">
						<InputField
							label="Nome del Venditore"
							type="text"
							placeholder="Inserisci il nome del venditore"
							value={newSellerName}
							onChange={(e) => setNewSellerName(e.target.value)}
						/>
					</div>
					<div className="column">
						<InputField
							label="Short Name"
							type="text"
							placeholder="Inserisci short name (unico)"
							value={newSellerShortName}
							onChange={(e) => setNewSellerShortName(e.target.value)}
						/>
					</div>
				</div>
				<div className="has-text-centered">
					<button className="button is-primary is-rounded mt-0" type="submit">
						Aggiungi Venditore
					</button>
				</div>
			</form>

			<div>
				<h2 className="subtitle">Venditori Esistenti</h2>
				<div className="my-5">
					<InputField
						type="text"
						placeholder="Cerca venditore..."
						value={searchTerm}
						onChange={handleSearchChange}
					/>
				</div>
				{/* Visualizzazione dei venditori filtrati */}
				<div className="table-container">
					<table className="table is-fullwidth is-hoverable">
						<thead>
							<tr>
								<th>Nome</th>
								<th>Fatture</th>
								<th>Azioni</th>
							</tr>
						</thead>
						<tbody>
							{filteredSellers.map((seller) => (
								<tr key={seller.id}>
									<td
										className="is-clickable"
										onClick={() => handleClickRow(seller.id)}
									>
										{seller.name}
									</td>
									<td
										className="is-clickable"
										onClick={() => handleClickRow(seller.id)}
									>
										{seller.invoiceCount}
									</td>
									<td>
										<button
											className="button is-outlined is-info is-small mr-1"
											onClick={() => handleEditSeller(seller)}
										>
											<FaEdit />
										</button>
										{seller.invoiceCount === 0 && (
											<button
												className="button is-outlined is-danger is-small"
												onClick={() => handleDeleteSeller(seller.id)}
											>
												<FaTrashAlt />
											</button>
										)}
									</td>
								</tr>
							))}
							{filteredSellers.length === 0 && (
								<tr className="has-text-centered">
									<td colSpan="3">Nessun Venditore Trovato</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</div>

			{/* Modifica Venditore */}
			{editingSeller && (
				<div className="modal is-active">
					<div className="modal-background" onClick={() => setEditingSeller(null)}></div>
					<div className="modal-content">
						<div className="box">
							<h2 className="subtitle">Modifica Venditore</h2>
							<div className="field">
								<label className="label">Nome del Venditore</label>
								<div className="control">
									<input
										className="input"
										type="text"
										value={editingSellerName}
										onChange={(e) => setEditingSellerName(e.target.value)}
									/>
								</div>
							</div>
							{errorEditingSeller && (
								<p className="help is-danger is-size-5 has-text-centered mx-4">
									{errorEditingSeller}
								</p>
							)}
							<div className="control has-text-right">
								<button
									className="button is-secondary mr-2"
									onClick={() => setEditingSeller(null)}
								>
									Annulla
								</button>
								<button className="button is-primary" onClick={handleSaveEdit}>
									Salva Modifiche
								</button>
							</div>
						</div>
					</div>
					<button
						className="modal-close is-large"
						onClick={() => setEditingSeller(null)}
					></button>
				</div>
			)}
		</AuthenticatedLayout>
	);
};

export default SellerPage;
